import React, { useEffect, useState, useMemo } from 'react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/Table';
import { Badge } from '@/components/ui/Badge';
import { FilterBar } from '@/components/ui/FilterBar';
import { api } from '@/api/client';
import { useAuth } from '@/context/AuthContext';
import { useCurrency, formatCurrency } from '@/hooks/useCurrency';
import { printDocument } from '@/utils/printDocs';
import { QuotationForm } from '@/components/forms/QuotationForm';
import { Printer, Plus, Edit, Trash, ArrowRightCircle } from 'lucide-react';

const QUOTATION_STATUSES = ['Draft', 'Sent', 'Accepted', 'Rejected', 'Expired', 'Converted'];

const computeTotal = (q) => {
  const sub = (q.items || []).reduce((s, it) => s + (it.qty || 0) * (it.price || 0), 0);
  const after = Math.max(0, sub - (q.discount || 0));
  return after * (1 + (q.tax || 0) / 100);
};

export const Quotations = () => {
  const currency = useCurrency();
  const { isAdmin, isSuperAdmin } = useAuth();
  const [quotations, setQuotations] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    try {
      const res = await api.getQuotations();
      setQuotations(res.data.sort((a, b) => new Date(b.created_at) - new Date(a.created_at)));
    } catch (e) { console.error(e); }
  };

  const handleNew = () => { setEditing(null); setShowForm(true); };
  const handleEdit = (q) => {
    if (q.status === 'Converted') {
      alert('Converted quotations cannot be edited. View the linked invoice instead.');
      return;
    }
    setEditing(q); setShowForm(true);
  };
  const handleDelete = async (q) => {
    if (!window.confirm(`Delete quotation ${q.qt_number}?`)) return;
    try { await api.deleteQuotation(q.id); loadAll(); } catch (e) { alert('Failed'); }
  };

  const handleConvert = async (q) => {
    if (q.status === 'Converted') {
      alert(`Already converted to invoice ${q.converted_invoice_id?.slice(0, 8).toUpperCase()}`);
      return;
    }
    if (!window.confirm(`Convert quotation ${q.qt_number} into an invoice?`)) return;
    try {
      const res = await api.convertQuotation(q.id);
      alert(`Created invoice ${res.data.invoice_id.slice(0, 8).toUpperCase()} — total ${formatCurrency(res.data.total, currency)}`);
      loadAll();
    } catch (e) {
      alert('Conversion failed');
    }
  };

  const handlePrint = (q) => {
    const total = computeTotal(q);
    const subtotal = (q.items || []).reduce((s, it) => s + (it.qty || 0) * (it.price || 0), 0);
    const taxAmt = Math.max(0, subtotal - (q.discount || 0)) * ((q.tax || 0) / 100);
    const rows = (q.items || []).map((it, i) => `
      <tr>
        <td>${i + 1}</td>
        <td><strong>${it.product_name || ''}</strong>${it.description ? `<br><span style="font-size:11px;color:#666">${it.description}</span>` : ''}</td>
        <td>${it.qty}</td>
        <td>${formatCurrency(it.price, currency)}</td>
        <td>${formatCurrency((it.qty || 0) * (it.price || 0), currency)}</td>
      </tr>
    `).join('');
    const content = `
      <div class="doc-title-bar">
        <div class="doc-title">Quotation</div>
        <div class="doc-meta">
          <div><strong>Quote #:</strong> ${q.qt_number || ''}</div>
          <div><strong>Date:</strong> ${new Date(q.created_at).toLocaleDateString()}</div>
          ${q.valid_until ? `<div><strong>Valid Until:</strong> ${q.valid_until}</div>` : ''}
        </div>
      </div>
      <div class="info-grid">
        <div class="info-card">
          <h3>Quoted To</h3>
          <p><strong>${q.customer_name || ''}</strong></p>
          ${q.customer_phone ? `<p>${q.customer_phone}</p>` : ''}
          ${q.customer_email ? `<p>${q.customer_email}</p>` : ''}
        </div>
        <div class="info-card">
          <h3>Status</h3>
          <p><strong>${q.status}</strong></p>
        </div>
      </div>
      <table class="doc-table">
        <thead>
          <tr><th>#</th><th>Item</th><th>Qty</th><th>Price</th><th>Total</th></tr>
        </thead>
        <tbody>${rows}</tbody>
      </table>
      <div class="totals">
        <div class="row"><span>Subtotal:</span><span>${formatCurrency(subtotal, currency)}</span></div>
        ${q.discount ? `<div class="row"><span>Discount:</span><span>-${formatCurrency(q.discount, currency)}</span></div>` : ''}
        ${q.tax ? `<div class="row"><span>Tax (${q.tax}%):</span><span>${formatCurrency(taxAmt, currency)}</span></div>` : ''}
        <div class="row total"><span>Grand Total:</span><span>${formatCurrency(total, currency)}</span></div>
      </div>
      ${q.notes ? `<div class="info-card" style="margin-top:18px"><h3>Notes</h3><p>${q.notes}</p></div>` : ''}
      <div class="signature-grid">
        <div class="sig-box">Customer Acceptance</div>
        <div class="sig-box">Authorized Signature</div>
      </div>
    `;
    printDocument({ title: q.qt_number || 'Quotation', content, docType: 'quotation' });
  };

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return quotations.filter(quo => {
      if (statusFilter && quo.status !== statusFilter) return false;
      if (!q) return true;
      return (quo.qt_number || '').toLowerCase().includes(q)
        || (quo.customer_name || '').toLowerCase().includes(q)
        || (quo.customer_phone || '').includes(q)
        || (quo.customer_email || '').toLowerCase().includes(q);
    });
  }, [quotations, search, statusFilter]);

  const statusVariant = (s) => ({
    'Draft': 'default',
    'Sent': 'info',
    'Accepted': 'success',
    'Rejected': 'danger',
    'Expired': 'warning',
    'Converted': 'success'
  })[s] || 'default';

  return (
    <div className="space-y-6" data-testid="quotations-page">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-medium tracking-tight text-[var(--text-primary)]">Quotations</h1>
        <Button onClick={handleNew} data-testid="new-quotation-button">
          <Plus size={18} className="mr-2" /> New Quotation
        </Button>
      </div>

      <Card>
        <FilterBar
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search QT #, customer name, phone, email..."
          filters={[{ key: 'status', label: 'All Statuses', value: statusFilter, options: QUOTATION_STATUSES, onChange: setStatusFilter }]}
          onClear={() => { setSearch(''); setStatusFilter(''); }}
          testId="quotation-filter"
        />
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>QT #</TableHead>
              <TableHead>Customer</TableHead>
              <TableHead>Phone</TableHead>
              <TableHead>Items</TableHead>
              <TableHead>Grand Total</TableHead>
              <TableHead>Valid Until</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Date</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.length === 0 ? (
              <TableRow><TableCell colSpan={9} className="text-center py-8 text-[var(--text-muted)]">No quotations yet</TableCell></TableRow>
            ) : filtered.map(q => (
              <TableRow key={q.id}>
                <TableCell className="font-mono text-xs font-semibold">{q.qt_number}</TableCell>
                <TableCell>{q.customer_name}</TableCell>
                <TableCell>{q.customer_phone || '—'}</TableCell>
                <TableCell>{q.items?.length || 0}</TableCell>
                <TableCell className="font-semibold">{formatCurrency(computeTotal(q), currency)}</TableCell>
                <TableCell>{q.valid_until || '—'}</TableCell>
                <TableCell><Badge variant={statusVariant(q.status)}>{q.status}</Badge></TableCell>
                <TableCell>{new Date(q.created_at).toLocaleDateString()}</TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <Button size="sm" variant="ghost" onClick={() => handleEdit(q)} data-testid={`edit-qt-${q.id}`}><Edit size={16} /></Button>
                    <Button size="sm" variant="ghost" onClick={() => handlePrint(q)} data-testid={`print-qt-${q.id}`}><Printer size={16} /></Button>
                    <Button size="sm" variant="ghost" onClick={() => handleConvert(q)} title="Convert to Invoice" data-testid={`convert-qt-${q.id}`}>
                      <ArrowRightCircle size={16} className="text-emerald-600" />
                    </Button>
                    {isSuperAdmin() && (
                      <Button size="sm" variant="ghost" onClick={() => handleDelete(q)} data-testid={`delete-qt-${q.id}`}>
                        <Trash size={16} className="text-red-600" />
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <QuotationForm isOpen={showForm} onClose={() => setShowForm(false)} quotation={editing} onSaved={loadAll} />
    </div>
  );
};
